import React, { useEffect, useState, useMemo, useRef } from "react";
import { DEPARTMENT_MEMBER, PERMIKA_TEAM, POSSIBLE_YEARS } from "../../../shared/data/team";
import { useYear } from "../About";
const MAX_DISPLAY = 27;

const MemberCard = React.memo(({ member }: { member: DEPARTMENT_MEMBER }) => {
    return (
        <div className="aspect-[5/4] overflow-hidden shadow-sm md:shadow-md md:hover:shadow-lg transition-shadow duration-300 [content-visibility:auto]">
            <img
                src={member.image}
                alt={member.name}
                loading="lazy"
                className="w-full h-full object-cover md:hover:scale-110 transition-transform duration-300"
            />
        </div>
    );
});

export const Gallery = () => {
    const { currentYear } = useYear();
    const currentYearLabel = POSSIBLE_YEARS[currentYear];
    const teamData = PERMIKA_TEAM[POSSIBLE_YEARS[currentYear]];
    const totalMembers = teamData.departmentMember.length;
    const [startIndex, setStartIndex] = useState(0);
    const [isMdUp, setIsMdUp] = useState<boolean>(typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true);
    const [inView, setInView] = useState<boolean>(true);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // track viewport size to disable rotation on small screens
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(min-width: 768px)');
        const handleChange = () => setIsMdUp(mq.matches);
        handleChange();
        mq.addEventListener?.('change', handleChange);
        return () => mq.removeEventListener?.('change', handleChange);
    }, []);

    useEffect(() => {
        // observe if the gallery is in viewport; pause rotation when not visible
        if (!containerRef.current || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                setInView(entry.isIntersecting);
            },
            { root: null, threshold: 0.1 }
        );
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (totalMembers <= MAX_DISPLAY) return;
        // Only rotate on md+ and when the section is visible to reduce work during scroll
        if (!isMdUp || !inView) return;

        let isCancelled = false;

        const rotate = () => {
            if (isCancelled) return;

            setStartIndex((prev) => (prev + 1) % totalMembers);

            // use set timeout instead of interval to recursively rotate
            setTimeout(rotate, 2500);
        };

        // initial 
        const timeoutId = setTimeout(rotate, 2500);

        return () => {
            isCancelled = true;
            clearTimeout(timeoutId);
        };
    }, [totalMembers, isMdUp, inView]);


    const displayedMembers = useMemo(() => {
        const length = Math.min(MAX_DISPLAY, totalMembers);
        const members = [];

        for (let i = 0; i < length; i++) {
            const index = (startIndex + i) % totalMembers;
            members.push(teamData.departmentMember[index]);
        }

        return members;
    }, [startIndex, totalMembers, teamData.departmentMember]);

    return (
        <div className="min-h-screen bg-white text-black flex flex-col items-center justify-center" ref={containerRef}>
            <div className="w-11/12 md:w-4/5 lg:w-2/3">
                {/* Mobile/simple grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:hidden">
                    {displayedMembers.map((member) => (
                        <MemberCard key={member.name} member={member} />
                    ))}
                </div>

                {/* Desktop/staggered grid */}
                <div className="hidden md:grid grid-cols-7 gap-4 h-full">
                    <div className="contents">
                        <div></div>
                        <div></div>
                        {displayedMembers.slice(0, 5).map((member) => (
                            <MemberCard key={member.name} member={member} />
                        ))}
                    </div>
                    <div className="contents">
                        <div></div>
                        <div></div>
                        <div></div>
                        {displayedMembers.slice(5, 9).map((member) => (
                            <MemberCard key={member.name} member={member} />
                        ))}
                    </div>
                    <div className="contents">
                        <div className="col-span-4 flex items-center justify-center">
                            <div className="text-center">
                                <h1 className="text-4xl md:text-5xl lg:text-[40px]">
                                    Meet PERMIKA <span className="text-[#CC7200]">{currentYearLabel}</span> team,
                                </h1>
                            </div>
                        </div>
                        {displayedMembers.slice(9, 12).map((member) => (
                            <MemberCard key={member.name} member={member} />
                        ))}
                    </div>
                    <div className="contents">
                        <div></div>
                        <div></div>
                        {displayedMembers.slice(12, 17).map((member) => (
                            <MemberCard key={member.name} member={member} />
                        ))}
                    </div>
                    <div className="contents">
                        <div></div>
                        <div></div>
                        {displayedMembers.slice(17, 22).map((member) => (
                            <MemberCard key={member.name} member={member} />
                        ))}
                    </div>
                    <div className="contents">
                        <div></div>
                        <div></div>
                        {displayedMembers.slice(22, 27).map((member) => (
                            <MemberCard key={member.name} member={member} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

